// hooks/useFormLoteFinanciera.js

import { useState, useCallback, useEffect } from "react";
import {
  getConciliationKeys,
  uploadConciliationFiles,
} from "modules/finops/conciliaciones/services/financieraService";

export const useFormLoteFinanciera = (idConcil = null, code = null, keyConcil = null) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [duplicateInFile, setDuplicateInFile] = useState([]);
  const [duplicateInDatabase, setDuplicateInDatabase] = useState([]);
  const [validationDetails, setValidationDetails] = useState(null);
  const [manualLlave, setSelectedLlave] = useState(null);
  const fixedLlave = String(keyConcil ?? "").trim();
  const selectedLlave = fixedLlave || manualLlave;
  const [llaveOptions, setLlaveOptions] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadConciliationKeys = useCallback(async () => {
    setLoadingKeys(true);
    try {
      const result = await getConciliationKeys();
      if (result.success) {
        setLlaveOptions(result.data);
      } else {
        setSnackbar({
          open: true,
          message: "Error al cargar llaves de conciliación",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error cargando llaves:", error);
      setSnackbar({
        open: true,
        message: "Error al cargar llaves de conciliación",
        severity: "error",
      });
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    if (!fixedLlave) loadConciliationKeys();
  }, [loadConciliationKeys, fixedLlave]);

  const handleLlaveChange = (event) => {
    const value = event.target.value;
    setSelectedLlave(value);

    if (selectedFiles.length > 0) {
      setSnackbar({
        open: true,
        message: "⚠️ Llave de conciliación cambiada. Por favor, revalide los archivos.",
        severity: "info",
      });
      setValidationErrors([]);
      setDuplicateInFile([]);
      setDuplicateInDatabase([]);
      setValidationDetails(null);
    }
  };

  const handleFileChange = async (files) => {
    if (!selectedLlave) {
      setSnackbar({
        open: true,
        message: "⚠️ Seleccione una llave de conciliación antes de cargar los archivos",
        severity: "error",
      });
      return;
    }

    const validExtensions = ["pdf", "jpeg", "jpg", "png"];
    const errors = [];
    const validFiles = [];

    for (const file of files) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        errors.push(`Formato no válido: ${file.name}. Solo se permiten PDF, JPEG y PNG`);
        continue;
      }

      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push(
          `El archivo ${file.name} es demasiado grande. Máximo 50MB permitidos por archivo`
        );
        continue;
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      setSnackbar({
        open: true,
        message: `❌ ${errors.length} archivos inválidos: ${errors.slice(0, 2).join(", ")}${
          errors.length > 2 ? ` y ${errors.length - 2} más` : ""
        }`,
        severity: "error",
      });
    }

    if (validFiles.length === 0) {
      return;
    }

    setSelectedFiles(validFiles);
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setValidating(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const validationResult = {
        isValid: true,
        totalRows: validFiles.length,
        validCount: validFiles.length,
        invalidCount: 0,
        errors: [],
        duplicateInFile: [],
        duplicateInDatabase: [],
      };

      setValidationDetails({
        totalRows: validationResult.totalRows || 0,
        validRows: validationResult.validCount || 0,
        invalidRows: validationResult.invalidCount || 0,
      });

      if (validationResult.isValid) {
        setValidationErrors([]);
        setDuplicateInFile([]);
        setDuplicateInDatabase([]);

        setSnackbar({
          open: true,
          message: `✅ ${validFiles.length} archivo(s) validado(s) para la llave ${selectedLlave}`,
          severity: "success",
        });
      } else {
        setValidationErrors(validationResult.errors || []);
        setDuplicateInFile(validationResult.duplicateInFile || []);
        setDuplicateInDatabase(validationResult.duplicateInDatabase || []);
        setSnackbar({
          open: true,
          message: `❌ El archivo contiene ${validationResult.errors?.length || 0} errores`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error en validación:", error);
      setValidationErrors([error.message || "Error al validar los archivos"]);
      setSnackbar({
        open: true,
        message: "Error al validar los archivos: " + (error.message || "Error desconocido"),
        severity: "error",
      });
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async (onImportSuccess, onError) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setSnackbar({
        open: true,
        message: "Seleccione al menos un archivo para subir",
        severity: "error",
      });
      return;
    }

    if (!selectedLlave) {
      setSnackbar({
        open: true,
        message: "Seleccione una llave de conciliación antes de subir",
        severity: "error",
      });
      return;
    }

    if (!idConcil) {
      setSnackbar({
        open: true,
        message: "El ID de la conciliación es requerido para subir archivos",
        severity: "error",
      });
      return;
    }

    if (!code) {
      setSnackbar({
        open: true,
        message: "El código (concesión) es requerido para subir archivos",
        severity: "error",
      });
      return;
    }

    if (validationErrors.length > 0) {
      setSnackbar({
        open: true,
        message: `Corrija los ${validationErrors.length} errores de validación antes de subir`,
        severity: "error",
      });
      return;
    }

    setImporting(true);
    setShowConfirmModal(true);
    setImportResult(null);
    setImportProgress(0);

    try {
      const result = await uploadConciliationFiles(
        selectedFiles,
        selectedLlave,
        idConcil,
        code,
        (progress) => {
          setImportProgress(progress);
        }
      );

      setImportResult(result);

      if (result.success) {
        if (onImportSuccess) {
          onImportSuccess(result);
        }
        setSnackbar({
          open: true,
          message:
            result.message ||
            `✅ ${selectedFiles.length} archivo(s) subido(s) exitosamente para la llave ${selectedLlave}`,
          severity: "success",
        });
      } else {
        if (onError) {
          onError(result.error);
        }
        setSnackbar({
          open: true,
          message: result.error || "Error al subir los archivos",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error en subida:", error);
      const errorMsg = error.message || "Error al subir";
      setImportResult({ success: false, error: errorMsg });

      if (onError) {
        onError(errorMsg);
      }

      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);

    if (importResult?.success) {
      setSelectedFiles([]);
      setValidationErrors([]);
      setDuplicateInFile([]);
      setDuplicateInDatabase([]);
      setValidationDetails(null);
      setImportProgress(0);

      const fileInput = document.getElementById("file-input-pdf");
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  const clearSelectedFile = () => {
    setSelectedFiles([]);
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setImportProgress(0);

    const fileInput = document.getElementById("file-input-pdf");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const resetForm = useCallback(() => {
    setSelectedFiles([]);
    setImporting(false);
    setImportResult(null);
    setShowConfirmModal(false);
    setValidationErrors([]);
    setValidating(false);
    setImportProgress(0);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setSelectedLlave(null);
    setSnackbar({
      open: false,
      message: "",
      severity: "success",
    });

    const fileInput = document.getElementById("file-input-pdf");
    if (fileInput) {
      fileInput.value = "";
    }
  }, []);

  return {
    selectedFiles,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    validationDetails,
    selectedLlave,
    llaveOptions,
    loadingKeys,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
    resetForm,
    handleLlaveChange,
    loadConciliationKeys,
  };
};
